import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Sparkles, Palette, Truck } from "lucide-react";
import { useStore } from "@/context/Store";
import { imgUrl } from "@/lib/api";
import { Container, Reveal, useSeo } from "@/components/site/common";
import ProductCard from "@/components/site/ProductCard";

const STEPS = [
  [Sparkles, "Escolhe", "Explora as nossas criações ou parte de uma ideia tua."],
  [Palette, "Personaliza", "Diz-nos medidas, cores e textos. Respondemos com proposta."],
  [Truck, "Recebe", "Produzimos com cuidado e enviamos para todo o país."],
];

function Hero({ products }) {
  const imgs = ["mural-abstrato-dourado", "acrilico-luminoso", "gaveta-renovada-vinil"].map((s) => products.find((p) => p.slug === s)).filter(Boolean);
  return (
    <section className="relative overflow-hidden">
      <div className="absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-[var(--pink)]/25 blur-3xl" />
      <div className="absolute -left-32 top-60 h-[380px] w-[380px] rounded-full bg-[var(--violet)]/15 blur-3xl" />
      <Container className="relative grid items-center gap-12 py-14 lg:grid-cols-12 lg:py-24">
        <div className="lg:col-span-6">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="eyebrow">Decoração · Comunicação visual · Eventos</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="mt-5 font-display text-5xl font-bold leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl" data-testid="hero-title">
            Ideias que <em className="text-gradient">fazem marca.</em>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.6 }} className="mt-6 max-w-md text-base leading-relaxed text-[var(--muted-ink)] sm:text-lg" data-testid="hero-subtitle">
            Transformar espaços em ambientes inspiradores, pensados para criar momentos e memórias, através de decoração, comunicação visual e personalização à medida.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-9 flex flex-wrap gap-3">
            <a href="#areas" className="btn-primary" data-testid="hero-explore-button">Explorar a loja <ArrowRight className="h-4 w-4" /></a>
            <Link to="/ideia" className="btn-ghost" data-testid="hero-idea-button">Tenho uma ideia</Link>
          </motion.div>
        </div>
        <div className="relative h-[420px] sm:h-[520px] lg:col-span-6">
          {imgs[0] && <motion.img initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }} src={imgUrl(imgs[0].images[0])} alt={imgs[0].alt} className="absolute right-0 top-0 h-[78%] w-[78%] rounded-[2rem] object-cover shadow-2xl" />}
          {imgs[1] && <motion.img initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.8 }} src={imgUrl(imgs[1].images[0])} alt={imgs[1].alt} className="absolute bottom-0 left-0 h-[52%] w-[46%] rounded-[1.5rem] border-[6px] border-[var(--paper)] object-cover shadow-xl" />}
          {imgs[2] && <motion.img initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4, duration: 0.8 }} src={imgUrl(imgs[2].images[0])} alt={imgs[2].alt} className="absolute bottom-4 right-6 hidden h-[34%] w-[30%] rounded-[1.25rem] border-[6px] border-[var(--paper)] object-cover shadow-xl sm:block" />}
        </div>
      </Container>
    </section>
  );
}

function Areas({ categories, products }) {
  return (
    <section id="areas" className="scroll-mt-24 py-16">
      <Container>
        <Reveal><p className="eyebrow">As nossas áreas</p><h2 className="mt-3 max-w-xl font-display text-4xl font-bold tracking-tight sm:text-5xl">Para cada espaço, <em className="text-gradient">ideias que…</em></h2></Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {categories.map((c, i) => {
            const n = products.filter((p) => p.category === c.slug).length;
            return (
              <Reveal key={c.slug} delay={i * 0.08}>
                <Link to={`/c/${c.slug}`} data-testid={`area-card-${c.slug}`} className="brand-gradient grain group flex min-h-[260px] flex-col justify-between overflow-hidden rounded-[2rem] p-7 text-white transition-transform duration-300 hover:-translate-y-1.5" style={{ filter: `hue-rotate(${i * -12}deg)` }}>
                  <div className="flex items-start justify-between"><span className="font-display text-xl font-medium italic text-white/90" data-testid={`area-card-verb-${c.slug}`}>…{c.verb}</span><ArrowUpRight className="h-6 w-6 transition-transform group-hover:rotate-45" /></div>
                  <div><p className="font-display text-3xl font-bold leading-tight">{c.obj}</p><p className="mt-2 text-sm text-white/85">{c.what}{n ? ` · ${n} ${n === 1 ? "produto" : "produtos"}` : ""}</p></div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </Container>
    </section>
  );
}

export default function Home() {
  const { categories, products } = useStore();
  useSeo(null, null);
  const featured = products.filter((p) => p.featured).slice(0, 8);
  return (
    <div data-testid="home-page">
      <Hero products={products} />
      <Areas categories={categories} products={products} />
      <section className="py-16">
        <Container>
          <Reveal className="flex flex-wrap items-end justify-between gap-4">
            <div><p className="eyebrow">Trabalhos em destaque</p><h2 className="mt-3 font-display text-4xl font-bold tracking-tight sm:text-5xl">Feitos pela DUSA.</h2></div>
            <Link to="/c/casa" className="text-sm font-semibold text-[var(--violet)] hover:underline">Ver tudo →</Link>
          </Reveal>
          <div className="mt-12 grid grid-cols-2 gap-x-5 gap-y-10 lg:grid-cols-4">{featured.map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}</div>
        </Container>
      </section>
      <section className="py-16">
        <Container>
          <div className="rounded-[2.5rem] bg-[var(--ink)] px-7 py-14 text-white sm:px-14">
            <Reveal><p className="eyebrow !text-[var(--pink)]">Como funciona</p><h2 className="mt-3 font-display text-4xl font-bold sm:text-5xl">Três passos, zero complicações.</h2></Reveal>
            <div className="mt-12 grid gap-10 md:grid-cols-3">
              {STEPS.map(([Icon, t, d], i) => (
                <Reveal key={t} delay={i * 0.1} className="border-t border-white/15 pt-6">
                  <div className="flex items-center gap-3"><span className="font-display text-5xl font-bold text-[var(--pink)]">0{i + 1}</span><Icon className="h-6 w-6 text-white/60" /></div>
                  <p className="mt-4 text-xl font-semibold">{t}</p><p className="mt-2 text-sm leading-relaxed text-white/70">{d}</p>
                </Reveal>
              ))}
            </div>
            <Link to="/ideia" className="btn-wa mt-12" data-testid="how-it-works-cta">Começar o meu pedido <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </Container>
      </section>
    </div>
  );
}
